// src/redux/slices/adminSlice.js - Complete Redux Implementation

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import adminAPI from '../../services/adminAPI';
import api from '../../services/api';

// ========================================
// 📊 DASHBOARD ASYNC THUNKS
// ========================================
export const fetchDashboardStats = createAsyncThunk(
  'admin/fetchDashboardStats',
  async (_, { rejectWithValue }) => {
    try {
      const response = await adminAPI.getDashboardStats();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch stats');
    }
  }
);

export const fetchRealTimeStats = createAsyncThunk(
  'admin/fetchRealTimeStats',
  async (_, { rejectWithValue }) => {
    try {
      const response = await adminAPI.getRealTimeStats();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch real-time stats');
    }
  }
);

export const fetchAnalytics = createAsyncThunk(
  'admin/fetchAnalytics',
  async (period, { rejectWithValue }) => {
    try {
      const response = await adminAPI.getAnalytics(period);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch analytics');
    }
  }
);

export const executeQuickAction = createAsyncThunk(
  'admin/executeQuickAction',
  async (action, { rejectWithValue }) => {
    try {
      const response = await adminAPI.executeQuickAction(action);
      return { action, result: response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Quick action failed');
    }
  }
);

// ========================================
// 👥 USER MANAGEMENT ASYNC THUNKS
// ========================================
export const fetchUsers = createAsyncThunk(
  'admin/fetchUsers',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await adminAPI.getUsers(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch users');
    }
  }
);

export const createUser = createAsyncThunk(
  'admin/createUser',
  async (userData, { rejectWithValue }) => {
    try {
      const response = await adminAPI.createUser(userData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create user');
    }
  }
);

export const updateUser = createAsyncThunk(
  'admin/updateUser',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await adminAPI.updateUser(id, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update user');
    }
  }
);

export const deleteUser = createAsyncThunk(
  'admin/deleteUser',
  async (id, { rejectWithValue }) => {
    try {
      await adminAPI.deleteUser(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete user');
    }
  }
);

// ========================================
// 🔄 BULK UPDATE USERS - ADDED ACTION
// ========================================
export const bulkUpdateUsers = createAsyncThunk(
  'admin/bulkUpdateUsers',
  async (payload, { rejectWithValue }) => {
    try {
      const response = await adminAPI.bulkUpdateUsers(payload);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Bulk update failed');
    }
  }
);

// ========================================
// 🏠 PROPERTY MANAGEMENT ASYNC THUNKS
// ========================================
export const fetchAdminProperties = createAsyncThunk(
  'admin/fetchAdminProperties',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await adminAPI.getProperties(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch properties');
    }
  }
);

// src/redux/slices/adminSlice.js - ENHANCED CREATE PROPERTY THUNK

export const createProperty = createAsyncThunk(
  'admin/createProperty',
  async (propertyData, { rejectWithValue }) => {
    try {
      
      
      // Validate data before API call
      if (!propertyData.title || !propertyData.description || !propertyData.price || !propertyData.location) {
        return rejectWithValue('Missing required fields: title, description, price, and location are required');
      }

      const response = await adminAPI.createProperty(propertyData);
      
      
      return response.data;
      
    } catch (error) {
      console.error('Redux createProperty error:', error);
      
      // Extract meaningful error message
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.errors?.[0]?.message ||
                          error.message || 
                          'Failed to create property';
      
      return rejectWithValue(errorMessage);
    }
  }
);

export const updateProperty = createAsyncThunk(
  'admin/updateProperty',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await adminAPI.updateProperty(id, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update property');
    }
  }
);

export const deleteProperty = createAsyncThunk(
  'admin/deleteProperty',
  async (id, { rejectWithValue }) => {
    try {
      await adminAPI.deleteProperty(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete property');
    }
  }
);

export const updatePropertyStatus = createAsyncThunk(
  'admin/updatePropertyStatus',
  async ({ id, status }, { rejectWithValue }) => {
    try {
      const response = await adminAPI.updatePropertyStatus(id, status);
      return { id, status, ...response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update status');
    }
  }
);

export const togglePropertyFeatured = createAsyncThunk(
  'admin/togglePropertyFeatured',
  async (id, { rejectWithValue }) => {
    try {
      const response = await adminAPI.togglePropertyFeatured(id);
      return { id, ...response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to toggle featured');
    }
  }
);

// ========================================
// 👨‍💼 AGENT MANAGEMENT ASYNC THUNKS
// ========================================
export const fetchAdminAgents = createAsyncThunk(
  'admin/fetchAdminAgents',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await adminAPI.getAgents(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch agents');
    }
  }
);

export const createAgent = createAsyncThunk(
  'admin/createAgent',
  async (agentData, { rejectWithValue }) => {
    try {
      const response = await adminAPI.createAgent(agentData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create agent');
    }
  }
);

export const updateAgent = createAsyncThunk(
  'admin/updateAgent',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await adminAPI.updateAgent(id, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update agent');
    }
  }
);

export const deleteAgent = createAsyncThunk(
  'admin/deleteAgent',
  async (id, { rejectWithValue }) => {
    try {
      await adminAPI.deleteAgent(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete agent');
    }
  }
);

// ========================================
// 📝 BLOG MANAGEMENT ASYNC THUNKS
// ========================================
export const fetchAdminBlogs = createAsyncThunk(
  'admin/fetchAdminBlogs',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await adminAPI.getBlogs(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch blogs');
    }
  }
);

// In your adminSlice.js or create separate admin blog actions
// In your adminSlice.js



export const createBlog = createAsyncThunk(
  'admin/createBlog',
  async (blogData, { rejectWithValue }) => {
    try {
      const response = await api.post('/admin/blogs', blogData);
      
      if (response.data.success) {
        return response.data;
      } else {
        return rejectWithValue(response.data.message || 'Failed to create blog');
      }
    } catch (error) {
      // Extract meaningful error message
      const message = 
        error.response?.data?.message || 
        error.response?.data?.error || 
        error.message || 
        'Network error - failed to create blog';
      
      console.error('Create blog error:', error);
      return rejectWithValue(message);
    }
  }
);

export const updateBlog = createAsyncThunk(
  'admin/updateBlog',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await adminAPI.updateBlog(id, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update blog');
    }
  }
);

export const deleteBlog = createAsyncThunk(
  'admin/deleteBlog',
  async (id, { rejectWithValue }) => {
    try {
      await adminAPI.deleteBlog(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete blog');
    }
  }
);

// ========================================
// 📞 CONTACT MANAGEMENT ASYNC THUNKS
// ========================================
export const fetchAdminContacts = createAsyncThunk(
  'admin/fetchAdminContacts',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await adminAPI.getContacts(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch contacts');
    }
  }
);

export const updateContactStatus = createAsyncThunk(
  'admin/updateContactStatus',
  async ({ id, status }, { rejectWithValue }) => {
    try {
      const response = await adminAPI.updateContactStatus(id, status);
      return { id, status, ...response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update contact status');
    }
  }
);

export const deleteContact = createAsyncThunk(
  'admin/deleteContact',
  async (id, { rejectWithValue }) => {
    try {
      await adminAPI.deleteContact(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete contact');
    }
  }
);

// ========================================
// 🤖 AI FEATURES ASYNC THUNKS
// ========================================
export const generateAIInsights = createAsyncThunk(
  'admin/generateAIInsights',
  async (_, { rejectWithValue }) => {
    try {
      const response = await adminAPI.generateAIInsights();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to generate AI insights');
    }
  }
);

export const generateAIContent = createAsyncThunk(
  'admin/generateAIContent',
  async ({ type, data }, { rejectWithValue }) => {
    try {
      const response = await adminAPI.generateAIContent(type, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to generate AI content');
    }
  }
);

// ========================================
// 📊 INITIAL STATE
// ========================================
const initialState = {
  // Dashboard
  dashboardStats: null,
  realTimeStats: null,
  analytics: null,
  dashboardLoading: false,
  realTimeLoading: false,
  analyticsLoading: false,

  // Users
  users: [],
  usersLoading: false,
  usersPagination: null,
  userOperationLoading: false,

  // Properties
  adminProperties: [],
  adminPropertiesLoading: false,
  propertyOperationLoading: false,
  propertiesPagination: null,

  // Agents
  adminAgents: [],
  adminAgentsLoading: false,
  agentOperationLoading: false,
  agentsPagination: null,

  // Blogs
  adminBlogs: [],
  adminBlogsLoading: false,
  blogOperationLoading: false,
  blogsPagination: null,

  // Contacts
  adminContacts: [],
  adminContactsLoading: false,
  contactOperationLoading: false,
  contactsPagination: null,

  // AI Features
  aiInsights: [],
  aiContent: null,
  aiLoading: false,

  // Global States
  error: null,
  success: null,
  lastUpdated: null,
  pendingOperations: 0,
  quickActionLoading: false,

  // Testimonials
adminTestimonials: [],
adminTestimonialsLoading: false,
testimonialOperationLoading: false,

};

// ========================================
// 🧬 ADMIN SLICE
// ========================================
const adminSlice = createSlice({
  name: 'admin',
  initialState,
  reducers: {
    // Global Actions
    clearError: (state) => {
      state.error = null;
    },
    clearSuccess: (state) => {
      state.success = null;
    },
    setLastUpdated: (state) => {
      state.lastUpdated = new Date().toISOString();
    },
    incrementPendingOperations: (state) => {
      state.pendingOperations += 1;
    },
    decrementPendingOperations: (state) => {
      state.pendingOperations = Math.max(0, state.pendingOperations - 1);
    },

    // Local Updates for Optimistic UI
    updatePropertyLocally: (state, action) => {
      const { id, updates } = action.payload;
      const propertyIndex = state.adminProperties.findIndex(prop => prop.id === id);
      if (propertyIndex !== -1) {
        state.adminProperties[propertyIndex] = {
          ...state.adminProperties[propertyIndex],
          ...updates
        };
      }
    },

    updateUserLocally: (state, action) => {
      const { id, updates } = action.payload;
      const userIndex = state.users.findIndex(user => user.id === id);
      if (userIndex !== -1) {
        state.users[userIndex] = {
          ...state.users[userIndex],
          ...updates
        };
      }
    },

    updateAgentLocally: (state, action) => {
      const { id, updates } = action.payload;
      const agentIndex = state.adminAgents.findIndex(agent => agent.id === id);
      if (agentIndex !== -1) {
        state.adminAgents[agentIndex] = {
          ...state.adminAgents[agentIndex],
          ...updates
        };
      }
    },
     toggleAgentStatus: (state, action) => {
      const agentId = action.payload;
      const agent = state.agents.find(agent => agent.id === agentId);
      if (agent) {
        agent.isActive = !agent.isActive;
      }
    },
  },

  extraReducers: (builder) => {
    builder
      // ========================================
      // 📊 DASHBOARD REDUCERS
      // ========================================
      .addCase(fetchDashboardStats.pending, (state) => {
        state.dashboardLoading = true;
        state.error = null;
      })
      .addCase(fetchDashboardStats.fulfilled, (state, action) => {
        state.dashboardLoading = false;
        state.dashboardStats = action.payload;
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(fetchDashboardStats.rejected, (state, action) => {
        state.dashboardLoading = false;
        state.error = action.payload;
      })

      .addCase(fetchRealTimeStats.pending, (state) => {
        state.realTimeLoading = true;
      })
      .addCase(fetchRealTimeStats.fulfilled, (state, action) => {
        state.realTimeLoading = false;
        state.realTimeStats = action.payload;
      })
      .addCase(fetchRealTimeStats.rejected, (state, action) => {
        state.realTimeLoading = false;
        state.error = action.payload;
      })

      .addCase(fetchAnalytics.pending, (state) => {
        state.analyticsLoading = true;
      })
      .addCase(fetchAnalytics.fulfilled, (state, action) => {
        state.analyticsLoading = false;
        state.analytics = action.payload;
      })
      .addCase(fetchAnalytics.rejected, (state, action) => {
        state.analyticsLoading = false;
        state.error = action.payload;
      })

      .addCase(executeQuickAction.pending, (state) => {
        state.quickActionLoading = true;
      })
      .addCase(executeQuickAction.fulfilled, (state, action) => {
        state.quickActionLoading = false;
        state.success = `${action.payload.action} completed successfully`;
      })
      .addCase(executeQuickAction.rejected, (state, action) => {
        state.quickActionLoading = false;
        state.error = action.payload;
      })

      // ========================================
      // 👥 USER REDUCERS
      // ========================================
      .addCase(fetchUsers.pending, (state) => {
        state.usersLoading = true;
        state.error = null;
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.usersLoading = false;
        state.users = action.payload.users || action.payload;
        state.usersPagination = action.payload.pagination;
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.usersLoading = false;
        state.error = action.payload;
      })

      .addCase(createUser.pending, (state) => {
        state.userOperationLoading = true;
      })
      .addCase(createUser.fulfilled, (state, action) => {
        state.userOperationLoading = false;
        state.users.unshift(action.payload);
        state.success = 'User created successfully';
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(createUser.rejected, (state, action) => {
        state.userOperationLoading = false;
        state.error = action.payload;
      })

      .addCase(updateUser.fulfilled, (state, action) => {
        const index = state.users.findIndex(user => user.id === action.payload.id);
        if (index !== -1) {
          state.users[index] = action.payload;
        }
        state.success = 'User updated successfully';
      })

      .addCase(deleteUser.fulfilled, (state, action) => {
        state.users = state.users.filter(user => user.id !== action.payload);
        state.success = 'User deleted successfully';
      })

      // ========================================
      // 🔄 BULK UPDATE USERS REDUCERS - ADDED
      // ========================================
      .addCase(bulkUpdateUsers.pending, (state) => {
        state.userOperationLoading = true;
        state.error = null;
      })
      .addCase(bulkUpdateUsers.fulfilled, (state, action) => {
        state.userOperationLoading = false;
        
        const { affectedUsers, action: bulkAction, userIds } = action.payload;
        
        if (bulkAction === 'delete') {
          state.users = state.users.map(user => 
            userIds.includes(user.id) 
              ? { ...user, isActive: false, deletedAt: new Date().toISOString() }
              : user
          );
        } else if (bulkAction === 'activate') {
          state.users = state.users.map(user => 
            userIds.includes(user.id) 
              ? { ...user, isActive: true }
              : user
          );
        } else if (bulkAction === 'deactivate') {
          state.users = state.users.map(user => 
            userIds.includes(user.id) 
              ? { ...user, isActive: false }
              : user
          );
        } else if (bulkAction === 'verify') {
          state.users = state.users.map(user => 
            userIds.includes(user.id) 
              ? { ...user, isVerified: true }
              : user
          );
        } else if (bulkAction === 'update_role') {
          state.users = state.users.map(user => 
            userIds.includes(user.id) 
              ? { ...user, role: action.meta.arg.updateData?.role || user.role }
              : user
          );
        }
        
        state.success = `Bulk ${bulkAction} completed for ${affectedUsers} users`;
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(bulkUpdateUsers.rejected, (state, action) => {
        state.userOperationLoading = false;
        state.error = action.payload;
      })

      // ========================================
      // 🏠 PROPERTY REDUCERS
      // ========================================
      .addCase(fetchAdminProperties.pending, (state) => {
        state.adminPropertiesLoading = true;
        state.error = null;
      })
      .addCase(fetchAdminProperties.fulfilled, (state, action) => {
        state.adminPropertiesLoading = false;
        state.adminProperties = action.payload.properties || action.payload;
        state.propertiesPagination = action.payload.pagination;
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(fetchAdminProperties.rejected, (state, action) => {
        state.adminPropertiesLoading = false;
        state.error = action.payload;
      })

      .addCase(createProperty.pending, (state) => {
        state.propertyOperationLoading = true;
        state.error = null;
        state.pendingOperations += 1;
      })
      .addCase(createProperty.fulfilled, (state, action) => {
        state.propertyOperationLoading = false;
        state.pendingOperations = Math.max(0, state.pendingOperations - 1);
        state.adminProperties.unshift(action.payload);
        state.success = 'Property created successfully';
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(createProperty.rejected, (state, action) => {
        state.propertyOperationLoading = false;
        state.pendingOperations = Math.max(0, state.pendingOperations - 1);
        state.error = action.payload;
      })

      .addCase(updateProperty.pending, (state) => {
        state.propertyOperationLoading = true;
      })
      .addCase(updateProperty.fulfilled, (state, action) => {
        state.propertyOperationLoading = false;
        const propertyIndex = state.adminProperties.findIndex(prop => prop.id === action.payload.id);
        if (propertyIndex !== -1) {
          state.adminProperties[propertyIndex] = {
            ...state.adminProperties[propertyIndex],
            ...action.payload,
            updatedAt: new Date().toISOString()
          };
        }
        state.success = 'Property updated successfully';
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(updateProperty.rejected, (state, action) => {
        state.propertyOperationLoading = false;
        state.error = action.payload;
      })

      .addCase(deleteProperty.fulfilled, (state, action) => {
        state.adminProperties = state.adminProperties.filter(prop => prop.id !== action.payload);
        state.success = 'Property deleted successfully';
        state.lastUpdated = new Date().toISOString();
      })

      .addCase(updatePropertyStatus.fulfilled, (state, action) => {
        const { id, status } = action.payload;
        const property = state.adminProperties.find(prop => prop.id === id);
        if (property) {
          property.status = status;
        }
        state.success = 'Property status updated successfully';
      })

      .addCase(togglePropertyFeatured.fulfilled, (state, action) => {
        const { id, featured } = action.payload;
        const property = state.adminProperties.find(prop => prop.id === id);
        if (property) {
          property.featured = featured;
        }
        state.success = 'Property featured status updated successfully';
      })

      // ========================================
      // 👨‍💼 AGENT REDUCERS
      // ========================================
      .addCase(fetchAdminAgents.pending, (state) => {
        state.adminAgentsLoading = true;
        state.error = null;
      })
      .addCase(fetchAdminAgents.fulfilled, (state, action) => {
        state.adminAgentsLoading = false;
        state.adminAgents = action.payload.agents || action.payload;
        state.agentsPagination = action.payload.pagination;
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(fetchAdminAgents.rejected, (state, action) => {
        state.adminAgentsLoading = false;
        state.error = action.payload;
      })

      .addCase(createAgent.fulfilled, (state, action) => {
        state.adminAgents.unshift(action.payload);
        state.success = 'Agent created successfully';
      })

      .addCase(updateAgent.fulfilled, (state, action) => {
        const index = state.adminAgents.findIndex(agent => agent.id === action.payload.id);
        if (index !== -1) {
          state.adminAgents[index] = action.payload;
        }
        state.success = 'Agent updated successfully';
      })

      .addCase(deleteAgent.fulfilled, (state, action) => {
        state.adminAgents = state.adminAgents.filter(agent => agent.id !== action.payload);
        state.success = 'Agent deleted successfully';
      })

      // ========================================
      // 📝 BLOG REDUCERS
      // ========================================
      .addCase(fetchAdminBlogs.pending, (state) => {
        state.adminBlogsLoading = true;
        state.error = null;
      })
      .addCase(fetchAdminBlogs.fulfilled, (state, action) => {
        state.adminBlogsLoading = false;
        state.adminBlogs = action.payload.blogs || action.payload;
        state.blogsPagination = action.payload.pagination;
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(fetchAdminBlogs.rejected, (state, action) => {
        state.adminBlogsLoading = false;
        state.error = action.payload;
      })

      .addCase(createBlog.fulfilled, (state, action) => {
        state.adminBlogs.unshift(action.payload);
        state.success = 'Blog created successfully';
      })

      .addCase(updateBlog.fulfilled, (state, action) => {
        const index = state.adminBlogs.findIndex(blog => blog.id === action.payload.id);
        if (index !== -1) {
          state.adminBlogs[index] = action.payload;
        }
        state.success = 'Blog updated successfully';
      })

      .addCase(deleteBlog.fulfilled, (state, action) => {
        state.adminBlogs = state.adminBlogs.filter(blog => blog.id !== action.payload);
        state.success = 'Blog deleted successfully';
      })

      // ========================================
      // 📞 CONTACT REDUCERS
      // ========================================
      .addCase(fetchAdminContacts.pending, (state) => {
        state.adminContactsLoading = true;
        state.error = null;
      })
      .addCase(fetchAdminContacts.fulfilled, (state, action) => {
        state.adminContactsLoading = false;
        state.adminContacts = action.payload.contacts || action.payload;
        state.contactsPagination = action.payload.pagination;
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(fetchAdminContacts.rejected, (state, action) => {
        state.adminContactsLoading = false;
        state.error = action.payload;
      })

      .addCase(updateContactStatus.fulfilled, (state, action) => {
        const { id, status } = action.payload;
        const contact = state.adminContacts.find(contact => contact.id === id);
        if (contact) {
          contact.status = status;
        }
        state.success = 'Contact status updated successfully';
      })

      .addCase(deleteContact.fulfilled, (state, action) => {
        state.adminContacts = state.adminContacts.filter(contact => contact.id !== action.payload);
        state.success = 'Contact deleted successfully';
      })

      // ========================================
      // 🤖 AI REDUCERS
      // ========================================
      .addCase(generateAIInsights.pending, (state) => {
        state.aiLoading = true;
      })
      .addCase(generateAIInsights.fulfilled, (state, action) => {
        state.aiLoading = false;
        state.aiInsights = action.payload;
      })
      .addCase(generateAIInsights.rejected, (state, action) => {
        state.aiLoading = false;
        state.error = action.payload;
      })

      .addCase(generateAIContent.pending, (state) => {
        state.aiLoading = true;
      })
      .addCase(generateAIContent.fulfilled, (state, action) => {
        state.aiLoading = false;
        state.aiContent = action.payload;
        state.success = 'AI content generated successfully';
      })
      .addCase(generateAIContent.rejected, (state, action) => {
        state.aiLoading = false;
        state.error = action.payload;
      })
      // TESTIMONIAL REDUCERS
.addCase(fetchAdminTestimonials.pending, (state) => {
  state.adminTestimonialsLoading = true;
  state.error = null;
})
.addCase(fetchAdminTestimonials.fulfilled, (state, action) => {
  state.adminTestimonialsLoading = false;
  state.adminTestimonials = action.payload.testimonials || action.payload;
  state.lastUpdated = new Date().toISOString();
})
.addCase(fetchAdminTestimonials.rejected, (state, action) => {
  state.adminTestimonialsLoading = false;
  state.error = action.payload;
})
.addCase(deleteTestimonial.fulfilled, (state, action) => {
  state.adminTestimonials = state.adminTestimonials.filter(t => t.id !== action.payload);
  state.success = 'Testimonial deleted successfully';
})
.addCase(updateTestimonial.fulfilled, (state, action) => {
  const index = state.adminTestimonials.findIndex(t => t.id === action.payload.id);
  if (index !== -1) {
    state.adminTestimonials[index] = action.payload;
  }
  state.success = 'Testimonial updated successfully';
});

  }
});

// ========================================
// 🎯 ACTIONS EXPORT
// ========================================
// TESTIMONIAL MANAGEMENT ASYNC THUNKS
export const fetchAdminTestimonials = createAsyncThunk(
  'admin/fetchAdminTestimonials',
  async (params, { rejectWithValue }) => {
    try {
      const response = await api.get('/testimonials', { params });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch testimonials');
    }
  }
);

export const deleteTestimonial = createAsyncThunk(
  'admin/deleteTestimonial',
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`/testimonials/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete testimonial');
    }
  }
);

export const updateTestimonial = createAsyncThunk(
  'admin/updateTestimonial',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/testimonials/${id}`, data);
      return response.data.testimonial;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update testimonial');
    }
  }
);
 
export const {
  clearError,
  clearSuccess,
  setLastUpdated,
  incrementPendingOperations,
  decrementPendingOperations,
  updatePropertyLocally,
  updateUserLocally,
  updateAgentLocally,
  toggleAgentStatus
} = adminSlice.actions;

// ========================================
// 🎯 SELECTORS EXPORT
// ========================================

// Dashboard Selectors
export const selectDashboardStats = (state) => state.admin.dashboardStats;
export const selectDashboardLoading = (state) => state.admin.dashboardLoading;
export const selectRealTimeStats = (state) => state.admin.realTimeStats;
export const selectAnalytics = (state) => state.admin.analytics;

// User Selectors
export const selectUsers = (state) => state.admin.users;
export const selectUsersLoading = (state) => state.admin.usersLoading;
export const selectUsersPagination = (state) => state.admin.usersPagination;
export const selectUserOperationLoading = (state) => state.admin.userOperationLoading;

// Property Selectors
export const selectAdminProperties = (state) => state.admin.adminProperties;
export const selectAdminPropertiesLoading = (state) => state.admin.adminPropertiesLoading;
export const selectPropertyOperationLoading = (state) => state.admin.propertyOperationLoading;
export const selectPropertiesPagination = (state) => state.admin.propertiesPagination;

// Agent Selectors
export const selectAdminAgents = (state) => state.admin.adminAgents;
export const selectAdminAgentsLoading = (state) => state.admin.adminAgentsLoading;
export const selectAgentOperationLoading = (state) => state.admin.agentOperationLoading;

// Blog Selectors
export const selectAdminBlogs = (state) => state.admin.adminBlogs;
export const selectAdminBlogsLoading = (state) => state.admin.adminBlogsLoading;
export const selectBlogOperationLoading = (state) => state.admin.blogOperationLoading;

// Contact Selectors
export const selectAdminContacts = (state) => state.admin.adminContacts;
export const selectAdminContactsLoading = (state) => state.admin.adminContactsLoading;

// AI Selectors
export const selectAIInsights = (state) => state.admin.aiInsights;
export const selectAIContent = (state) => state.admin.aiContent;
export const selectAILoading = (state) => state.admin.aiLoading;

// Global Selectors
export const selectAdminError = (state) => state.admin.error;
export const selectAdminSuccess = (state) => state.admin.success;
export const selectLastUpdated = (state) => state.admin.lastUpdated;
export const selectPendingOperations = (state) => state.admin.pendingOperations;
export const selectQuickActionLoading = (state) => state.admin.quickActionLoading;

// Computed Selectors
export const selectPropertyById = (state, id) =>
  state.admin.adminProperties.find(prop => prop.id === id);

export const selectUserById = (state, id) =>
  state.admin.users.find(user => user.id === id);

export const selectAgentById = (state, id) =>
  state.admin.adminAgents.find(agent => agent.id === id);

export const selectBlogById = (state, id) =>
  state.admin.adminBlogs.find(blog => blog.id === id);

export const selectContactById = (state, id) =>
  state.admin.adminContacts.find(contact => contact.id === id);
// TESTIMONIAL SELECTORS (add these right before "export default adminSlice.reducer;")
export const selectAdminTestimonials = (state) => state.admin.adminTestimonials || [];
export const selectAdminTestimonialsLoading = (state) => state.admin.adminTestimonialsLoading || false;
export const selectTestimonialOperationLoading = (state) => state.admin.testimonialOperationLoading || false;



export default adminSlice.reducer;
